import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, AdminHeader, Stat, Field, SelectField } from "@/pages/admin/_shared";
import { CurrencyDollar, ShoppingCart, Coins, Crown, Plus, Trash, ChartLineUp } from "@phosphor-icons/react";

export default function AdminMarketplace() {
  const [items, setItems] = useState([]);
  const [rev, setRev] = useState(null);
  const [form, setForm] = useState({ name: "", category: "preset", price_cents: 999, seller_name: "POOH", cover_color: "#FF003C", description: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [i, r] = await Promise.all([
      api.get("/admin/marketplace/items"),
      api.get("/admin/revenue/summary"),
    ]);
    setItems(i.data); setRev(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/marketplace/items", { ...form, price_cents: parseInt(form.price_cents) });
      setForm({ ...form, name: "", description: "" });
      load();
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/marketplace/items/${id}`);
    load();
  };

  if (!rev) return <div className="p-10 text-ink-dim">LOADING MARKETPLACE…</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-marketplace">
      <AdminHeader section="MARKETPLACE & REVENUE" title={["SELL ", "*SOUND."]} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <Stat label="TOTAL REVENUE" value={rev.total_revenue_display} Icon={CurrencyDollar} color="text-brand-green" testid="rev-total" />
        <Stat label="SUBSCRIPTION MRR" value={rev.subscription_mrr_display} Icon={Crown} testid="rev-mrr" />
        <Stat label="MARKETPLACE" value={rev.marketplace_revenue_display} Icon={ShoppingCart} color="text-brand-purple" testid="rev-market" />
        <Stat label="CREATOR PAYOUTS" value={rev.creator_payouts_display} Icon={Coins} color="text-brand-orange" testid="rev-payouts" />
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 mb-8">
        <div className="bg-bg-surface p-5">
          <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">PLATFORM COMMISSION (20%)</div>
          <div className="font-display font-black text-3xl tracking-tighter text-brand-red mt-2">{rev.platform_commission_display}</div>
        </div>
        <div className="bg-bg-surface p-5">
          <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">UNITS SOLD</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-2">{rev.sales_total.toLocaleString()}</div>
        </div>
        <div className="bg-bg-surface p-5">
          <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">AVG ORDER VALUE</div>
          <div className="font-display font-black text-3xl tracking-tighter text-brand-green mt-2">
            ${rev.sales_total ? (rev.marketplace_revenue_cents / rev.sales_total / 100).toFixed(2) : "0.00"}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Listing form */}
        <Panel title="LIST NEW ITEM" testid="market-form">
          <form onSubmit={create} className="space-y-3">
            <Field label="Item name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="VOID 808 VOL.2" testid="market-name" required />
            <SelectField label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={["preset", "sound_pack", "kit", "beat"]} testid="market-category" />
            <Field label="Price (cents)" type="number" value={form.price_cents} onChange={(v) => setForm({ ...form, price_cents: v })} testid="market-price" />
            <Field label="Seller name" value={form.seller_name} onChange={(v) => setForm({ ...form, seller_name: v })} testid="market-seller" />
            <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="market-desc" multiline />
            <div>
              <span className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">Cover color</span>
              <div className="flex gap-1.5 mt-1.5">
                {["#FF003C", "#8B00FF", "#FF6B00", "#00FFAA", "#FF00C8", "#00C2FF"].map((c) => (
                  <button type="button" key={c} onClick={() => setForm({ ...form, cover_color: c })} data-testid={`market-color-${c.slice(1)}`} className="w-7 h-7 border" style={{ background: c, borderColor: form.cover_color === c ? "#fff" : "#27272A" }} />
                ))}
              </div>
            </div>
            <button type="submit" disabled={busy || !form.name} data-testid="market-submit" className="w-full bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold py-3 hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Plus size={12} weight="bold" /> LIST ITEM
            </button>
          </form>
        </Panel>

        {/* Top sellers */}
        <Panel title="TOP SELLERS" tag={`${items.length} ITEMS`} className="lg:col-span-2" testid="market-list">
          {items.length === 0 ? (
            <div className="text-xs text-ink-dim py-10 text-center">No marketplace items yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 max-h-[600px] overflow-y-auto">
              {items.map((it) => (
                <div key={it.id} className="bg-bg-surface p-4 group hover:bg-bg-surface2" data-testid={`market-item-${it.id}`}>
                  <div className="h-12 w-full mb-3 flex items-end px-2 pb-1" style={{ background: `linear-gradient(135deg, ${it.cover_color}, #050505)` }}>
                    <span className="text-[9px] tracking-[0.3em] uppercase text-white/80">{it.category}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <div className="font-display font-bold tracking-tight text-sm truncate flex-1">{it.name}</div>
                    <button onClick={() => remove(it.id)} className="text-ink-mute hover:text-brand-red opacity-0 group-hover:opacity-100" data-testid={`market-del-${it.id}`}>
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-ink-dim mt-1">by {it.seller_name}</div>
                  <div className="flex items-baseline justify-between mt-3">
                    <span className="font-display text-xl font-black text-brand-red">{it.price_display}</span>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-ink-mute">{it.sales} sold</span>
                  </div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-brand-green mt-1">↑ ${(it.revenue_cents / 100).toFixed(2)} earned</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
